import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Loader2,
  Sparkles,
  MoreVertical,
  Search,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import { outreachTemplatesApi, type OutreachTemplate } from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

export default function OutreachTemplatesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [deleteTemplate, setDeleteTemplate] = useState<OutreachTemplate | null>(null)

  const { data: templates, isLoading } = useQuery({
    queryKey: ['outreach-templates'],
    queryFn: outreachTemplatesApi.list,
  })

  const generateMutation = useMutation({
    mutationFn: outreachTemplatesApi.generate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-templates'] })
      setShowGenerateConfirm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => outreachTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-templates'] })
      setDeleteTemplate(null)
    },
  })

  const items = templates ?? []

  // Filter templates based on search
  const filteredItems = items.filter((t) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      t.name.toLowerCase().includes(searchLower) ||
      t.role_label.toLowerCase().includes(searchLower) ||
      t.subject.toLowerCase().includes(searchLower)
    )
  })

  const handleGenerateClick = () => {
    if (items.length > 0) {
      setShowGenerateConfirm(true)
    } else {
      generateMutation.mutate()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Outreach Templates</h1>
          <p className="mt-1 text-muted-foreground">
            {items.length} template{items.length !== 1 ? 's' : ''} available
          </p>
        </div>
        <Button onClick={handleGenerateClick} disabled={generateMutation.isPending}>
          {generateMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Templates
            </>
          )}
        </Button>
      </div>

      {/* Alerts */}
      {generateMutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{String(generateMutation.error)}</AlertDescription>
        </Alert>
      )}

      {generateMutation.isSuccess && (
        <Alert variant="success" className="flex items-center border-l-4 border-l-green-500 [&>svg+div]:translate-y-0">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <AlertDescription className="flex items-center gap-2 py-0.5">
            <span className="font-medium">Success</span>
            <span className="text-green-600/90 dark:text-green-400/90">— Templates generated successfully.</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      {isLoading ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template Name</TableHead>
                <TableHead>Goal / Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={4}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground">No templates yet</h3>
            <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
              Generate up to 3 outreach templates tailored to your industry. The AI will
              create templates for different prospect roles (e.g. Sellers, Buyers, Traders).
            </p>
            <Button
              className="mt-6"
              onClick={handleGenerateClick}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Generate Templates
            </Button>
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No templates found matching &quot;{search}&quot;
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template Name</TableHead>
                <TableHead>Goal / Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((template) => (
                <TableRow
                  key={template.id}
                  className={cn('cursor-pointer', !template.is_active && 'opacity-60')}
                  onClick={() => navigate(`/outreach/templates/${template.id}`)}
                >
                  <TableCell>
                    <p className="font-medium text-foreground">{template.name}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{template.role_label}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {template.is_default && (
                        <Badge variant="outline">Default</Badge>
                      )}
                      {template.is_active ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="destructive">Inactive</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/outreach/templates/${template.id}`)}>
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteTemplate(template)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Generate confirmation when templates exist */}
      <AlertDialog open={showGenerateConfirm} onOpenChange={setShowGenerateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing templates?</AlertDialogTitle>
            <AlertDialogDescription>
              Generating new templates will replace all existing templates. This cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                'Generate'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTemplate}
        onOpenChange={(open) => !open && setDeleteTemplate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove &quot;{deleteTemplate?.name}&quot;. Existing outreach
              records that used this template will keep their content but lose the
              template link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTemplate && deleteMutation.mutate(deleteTemplate.id)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
